import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import styled, { css } from 'styled-components';
import { QUERIES } from '../constants/mediaQueries';

import { ColorConfig, ColorScheme, IconColor } from '../config/colorSchemes';
import { isActivePath } from '../utils/navigation';
import { isExternalLink, withSmoothScroll } from '../utils/linkUtils';
import Icon from '../components/Icon';
import { ButtonSize, ButtonProps, TextSize, ButtonIconPosition } from '../types/button';

/**
 * Size scaling factors for button dimensions
 * These multipliers are applied to base padding values to create consistent size variants
 * Each factor creates proportional scaling across the entire component
 */
const SIZES = {
  xxsmall: 0.5,
  xsmall: 0.65,
  small: 0.75,
  medium: 1, // Base size (no scaling)
  large: 1.25,
  xlarge: 1.5,
  xxlarge: 1.75,
};

/**
 * Font size mapping for each text size
 * Maps text size variants to specific font size CSS variables
 * This allows independent control of text size separate from button padding
 */
const FONT_SIZE_MAP: Record<TextSize, string> = {
  xxsmall: 'calc(var(--body-tiny-size) * 0.75)',
  xsmall: 'calc(var(--body-tiny-size))',
  small: 'var(--body-small-size)',
  medium: 'var(--body-base-size)',
  large: 'var(--body-large-size)',
  xlarge: 'calc(var(--body-large-size) * 1.5)',
  xxlarge: 'calc(var(--body-large-size) * 1.75)',
};

/**
 * Generates size-specific CSS for buttons based on the provided size
 * Now only handles padding - text size is handled separately
 *
 * @param size - The button size variant to generate styles for
 * @returns CSS styles with appropriate padding
 */
const getSizeStyles = (size: ButtonSize = 'medium') => {
  const factor = SIZES[size];
  return css`
    padding: calc(var(--button-padding-v) * ${factor}) calc(var(--button-padding-h) * ${factor});
  `;
};

/**
 * Generates text size-specific CSS for buttons based on the provided text size
 * This allows independent control of text size separate from button padding
 *
 * @param textSize - The text size variant to generate styles for
 * @returns CSS styles with appropriate font size
 */
const getTextSizeStyles = (textSize: TextSize = 'medium') => {
  return css`
    font-size: ${FONT_SIZE_MAP[textSize]};
  `;
};

/**
 * Generates color-specific CSS for buttons based on the provided color scheme and active state
 *
 * @param colorScheme - The color scheme to apply to the button
 * @param isActive - Whether the button is in an active/selected state
 * @returns CSS styles with appropriate colors for background, border, and text
 */
const getColorStyles = (colorScheme: ColorScheme = 'scheme1', isActive = false) => {
  const colors = ColorConfig[colorScheme];

  if (isActive) {
    return css`
      background-color: ${colors.activeBackground};
      border: ${colors.activeBorder};
      color: ${colors.activeText};
      cursor: pointer;

      &:hover {
        background-color: ${colors.activeBackground};
        border: ${colors.activeBorder};
        color: ${colors.activeText};
        transform: none;
      }
    `;
  }

  return css`
    background: ${colors.background};
    border: ${colors.border};
    color: ${colors.text};

    &:hover {
      background: ${colors.hoverBackground};
      border: ${colors.hoverBorder};
      color: ${colors.hoverText};
      transform: var(--hover-scale-button);

      & span {
        color: ${colors.hoverText} !important;
        transition: var(--transition-all-ease);
      }
    }
  `;
};

export interface StyledButtonProps {
  $colorScheme?: ColorScheme;
  $size?: ButtonSize;
  $textSize?: TextSize;
  $isActive?: boolean;
  $iconColour?: IconColor;
  $iconPosition?: ButtonIconPosition;
  $showExternalLinkArrowIcon?: boolean;
  className?: string;
  disabled?: boolean;
}

// Shared button styles
const buttonStyles = css<StyledButtonProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: var(--font-family);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: var(--body-line);
  cursor: pointer;
  transition:
    var(--transition-all-ease),
    box-shadow 0.3s ease;
  border-radius: var(--radius-button);
  border-style: solid;
  width: fit-content;
  position: relative;
  overflow: hidden;
  transform-origin: center;
  text-decoration: none;
  white-space: nowrap;

  /* Apply color styles */
  ${(props) => getColorStyles(props.$colorScheme, props.$isActive)}

  /* Apply size-based styles (padding only) */
  ${(props) => getSizeStyles(props.$size)}

  /* Apply text size styles (font-size only) */
  ${(props) => getTextSizeStyles(props.$textSize)}

  /* SVG icon styling based on iconColour or colorScheme */
  ${(props) => {
    const iconColorScheme = props.$iconColour || props.$colorScheme || 'scheme1';
    const iconColors = ColorConfig[iconColorScheme];
    const isActive = props.$isActive;

    return css`
      svg {
        fill: ${isActive ? iconColors.activeText : iconColors.text};
        transition: var(--transition-all-ease);
      }

      &:hover svg {
        fill: ${iconColors.hoverText};
      }
    `;
  }}

  /* Icon positioning styles */
  ${(props) => {
    if (props.$iconPosition === 'icon-leading' || props.$iconPosition === 'icon-trailing') {
      return css`
        gap: var(--button-gap-icon);
      `;
    }

    if (props.$iconPosition === 'icon-only') {
      return css`
        padding: calc(var(--button-padding-icon) * ${SIZES[props.$size || 'medium']});
        aspect-ratio: 1 / 1;
        border-radius: var(--radius-button);

        &:hover {
          transform: var(--hover-scale-button-icon);
        }
      `;
    }

    return '';
  }}

  /* External link styles */
  ${(props) =>
    props.$showExternalLinkArrowIcon !== false
      ? css`
          &[target='_blank']::after {
            content: '↗';
            margin-left: var(--spacing-xsmall);
            font-size: var(--body-small-size);
            display: inline-block;
            transition: var(--transition-all-ease);
          }

          &[target='_blank']:hover::after {
            transform: var(--hover-translate-y);
          }
        `
      : ''}

  /* Disabled Button States */
  &:disabled,
  &[disabled],
  &.btn--disabled {
    opacity: 0.65;
    cursor: not-allowed;
    pointer-events: none;
    box-shadow: none;
  }

  /* Button Responsive Styles */
  ${QUERIES.maxMobile} {
    font-size: var(--body-base-size);
  }
`;

/**
 * Styled Component Variants
 * Each component applies the shared buttonStyles to a different HTML element
 * This allows consistent styling across various functional button types
 */

// Regular button element for form submissions and click actions
const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
`;

// Anchor tag for both hash links and external links
const StyledAnchorTag = styled.a<StyledButtonProps>`
  ${buttonStyles}
`;

// Gatsby Link component for internal page navigation with client-side routing
const StyledGatsbyLink = styled(GatsbyLink)<StyledButtonProps>`
  ${buttonStyles}
`;

/**
 * ButtonWrapper - Reusable container for button groups
 * Provides consistent grid layout and responsive behavior for multiple buttons
 */
export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-large);
  width: 100%;
  margin-top: var(--spacing-xlarge);

  ${QUERIES.maxTablet} {
    flex-direction: column;
    align-items: center;
  }
`;

const Button: React.FC<ButtonProps> = ({
  // Content props
  text,
  children,

  // Style props
  colorScheme = 'scheme1',
  size = 'medium',
  textSize = 'medium',
  disabled = false,
  className = '',

  // Icon props
  iconName,
  iconSize = 'medium',
  iconColour,
  iconPosition = null,
  showExternalLinkArrowIcon = true,

  // Button behavior props
  type,
  onClick,

  // Link behavior props
  href,
  target,
  rel,

  // Accessibility and identification
  id,
  role,
  isActiveOverride = false,
}) => {
  /**
   * Renders the appropriate content for the button based on icon placement
   * Handles various icon positions (leading, trailing, icon-only) and text content
   *
   * @returns React node with properly formatted content and icons
   */
  const renderContent = () => {
    if (!iconName || !iconPosition) {
      return text || children;
    }

    const icon = (
      <Icon name={iconName} alt={`Icon: ${iconName}`} colour={iconColour} size={iconSize} />
    );

    if (iconPosition === 'icon-only') {
      return icon;
    }

    return (
      <>
        {iconPosition === 'icon-leading' && icon}
        {text || children}
        {iconPosition === 'icon-trailing' && icon}
      </>
    );
  };

  /**
   * Common styling props shared across all button variants
   * These control the visual appearance regardless of the underlying HTML element
   */
  const commonProps = {
    $colorScheme: colorScheme,
    $size: size,
    $textSize: textSize,
    $iconColour: iconColour,
    $iconPosition: iconPosition,
    $showExternalLinkArrowIcon: showExternalLinkArrowIcon,
    className,
    disabled,
  };

  /**
   * Declarative component selection pattern
   * Determines the appropriate component and props based on button type and link characteristics
   * This approach centralizes the rendering logic and avoids multiple conditional returns
   */
  let ComponentType: any = StyledButton;
  let componentProps: any = {
    type,
    role: role || 'button',
    id,
    $isActive: isActiveOverride,
    onClick,
    ...commonProps,
  };

  if (!type && href) {
    /**
     * Link type detection
     * Categorizes links into three types with different behavior requirements:
     * 1. External links - Regular anchor tags that may open in new tabs
     * 2. Hash/anchor links - Regular anchor tags for same-page navigation and internal page anchors
     * 3. Internal page links - Gatsby Link components for client-side routing
     */
    const isExternal = isExternalLink(href);

    // Determine if this link contains a hash (anchor) - either #anchor or /path#anchor
    const hasHash = href.includes('#');

    if (hasHash) {
      // For any link containing a hash, use a regular anchor tag to preserve the fragment
      ComponentType = StyledAnchorTag;
      componentProps = {
        href,
        id,
        onClick,
        ...commonProps,
      };
    } else if (isExternal) {
      // For external links
      ComponentType = StyledAnchorTag;
      componentProps = {
        href,
        target,
        rel: rel || (target === '_blank' ? 'noopener noreferrer' : undefined),
        onClick,
        ...commonProps,
      };
    } else {
      // Internal Gatsby link for page navigation
      ComponentType = StyledGatsbyLink;
      componentProps = {
        to: href || '/',
        $isActive: (!!href && isActivePath(href)) || isActiveOverride,
        onClick: withSmoothScroll(href, onClick),
        ...commonProps,
      };
    }
  }

  return <ComponentType {...componentProps}>{renderContent()}</ComponentType>;
};

export default Button;
